var _excluded = ["checked", "className", "color", "defaultChecked", "disabled", "icon", "id", "label", "name", "onBlur", "onChange", "onFocus", "value"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import * as React from 'react';
import mergeRefs from 'react-merge-refs';
import IconCheckmark from '../icons/icon_checkmark';
import IconMinus from '../icons/icon_minus';
import useStyles from './use_styles';
import useFocusVisible from '../utils/use_focus_visible';
import { ariaCheckedStates, checkboxStates, checkedStatesToBool, checkboxStringProp } from './utils';
import propTypes from './prop_types';
import useControlled from '../utils/use_controlled';
import { jsx as ___EmotionJSX } from "@emotion/core";
var Checkbox = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  var {
      checked: checkedProp,
      className,
      color,
      defaultChecked: defaultCheckedProp = false,
      disabled = false,
      icon,
      id,
      label,
      name,
      onBlur,
      onChange,
      onFocus,
      value
    } = _ref,
    inputProps = _objectWithoutProperties(_ref, _excluded);
  var {
    handleBlurVisible,
    handleFocusVisible,
    ref: focusVisibleRef
  } = useFocusVisible();
  var handleRef = mergeRefs([ref, focusVisibleRef]);
  var [focusVisible, setFocusVisible] = React.useState(false);
  if (disabled && focusVisible) {
    setFocusVisible(false);
  }
  var defaultCheckedPropToBool = checkedStatesToBool(defaultCheckedProp);
  var [checked, setChecked, isControlled] = useControlled({
    controlled: checkedProp,
    default: defaultCheckedProp
  });
  var handleChange = React.useCallback(event => {
    if (disabled) return;
    setChecked(event.target.checked);
    if (onChange) onChange(event);
  }, [disabled, onChange, setChecked]);
  var handleKeyDown = React.useCallback(event => {
    if (event.key === ' ') {
      if (isControlled) event.preventDefault();
      handleChange(event);
    }
  }, [handleChange, isControlled]);
  var handleBlur = React.useCallback(event => {
    if (focusVisible) {
      setFocusVisible(false);
      handleBlurVisible();
    }
    if (onBlur) onBlur(event);
  }, [focusVisible, handleBlurVisible, onBlur]);
  var handleFocus = React.useCallback(event => {
    if (handleFocusVisible(event)) {
      setFocusVisible(true);
    }
    onFocus === null || onFocus === void 0 || onFocus(event);
  }, [handleFocusVisible, onFocus]);
  var hasIcon = icon !== undefined;
  var iconTemplate;
  if (hasIcon) {
    iconTemplate = /*#__PURE__*/React.cloneElement(icon, {
      className: 'vn-checkbox__span--custom_icon',
      color,
      customStyle: {
        padding: 0
      },
      disabled,
      filled: checkedStatesToBool(checked)
    });
  } else {
    var checkIcon;
    if (checkboxStates(checked) === checkboxStringProp.checked) {
      checkIcon = 'checkmark';
    } else if (checkboxStates(checked) === checkboxStringProp.indeterminated) {
      checkIcon = 'polygon';
    }
    iconTemplate = ___EmotionJSX(React.Fragment, null, ___EmotionJSX(IconCheckmark, {
      display: checkIcon === 'checkmark' ? 'block' : 'none',
      className: "vn-checkbox__span--icon",
      filled: true
    }), ___EmotionJSX(IconMinus, {
      size: 24,
      display: checkIcon === 'polygon' ? 'block' : 'none',
      className: "vn-checkbox__span--icon"
    }));
  }
  var CheckboxComponent = label ? 'label' : 'span';
  var style = useStyles({
    checked,
    disabled,
    focusVisible,
    hasIcon
  });
  return ___EmotionJSX(CheckboxComponent, {
    className: className,
    css: style.root
  }, ___EmotionJSX("span", {
    "aria-disabled": disabled,
    className: "vn-checkbox__span",
    tabIndex: disabled ? -1 : undefined
  }, ___EmotionJSX("input", _extends({
    "aria-checked": ariaCheckedStates(checked),
    checked: isControlled ? checkedStatesToBool(checked) : undefined,
    "data-indeterminate": checkboxStates(checked) === checkboxStringProp.indeterminated,
    defaultChecked: isControlled ? undefined : defaultCheckedPropToBool,
    disabled: disabled,
    id: id,
    name: name,
    onBlur: handleBlur,
    onChange: handleChange,
    onFocus: handleFocus,
    onKeyDown: handleKeyDown,
    ref: handleRef,
    type: "checkbox",
    value: value
  }, inputProps)), iconTemplate), label && ___EmotionJSX("span", {
    className: "label vn-checkbox__span--text"
  }, label));
});
Checkbox.propTypes = propTypes;
Checkbox.displayName = 'Checkbox';
export default Checkbox;